import { createContext } from '@fluentui/react-context-selector';
import type { RoutesWithApiEndpoint } from 'containers/App/routing/RoutesWithApiEndpoint';
import type { AppTranslations } from 'translations/translations';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import type { ShowNotificationCommand } from 'utils/hooks/useNotifications';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiHook } from 'utils/request/useApi';
import type {
  DeviceViewEntities,
  DeviceViewEntityId,
  DeviceViewSettings,
  DeviceViewWindows,
  PathsWithName,
  TabDataTables,
} from '../deviceViewTypes';
import type { HandlersContainer } from './HandlersContainer';

export interface DeviceViewState {
  activeDeviceViewEntityId: DeviceViewEntityId | null;
  deviceViewEntities: DeviceViewEntities;
  deviceViewOpen: boolean;
  deviceViewWindows: DeviceViewWindows;
  settings: DeviceViewSettings;
  tabData: TabDataTables;
}

export interface DeviceViewAdditionalCommandContext {
  api: ApiHook;
  msg: AppTranslations;
  showNotification: (command: ShowNotificationCommand) => void;
}

export interface DeviceViewOuterState {
  deviceViewContainer: HTMLElement | null;
  handlersContainer: HandlersContainer;
  pathsWithName: PathsWithName;
  routesWithApiEndpoint: RoutesWithApiEndpoint;
}

type S = DeviceViewState;
type C = DeviceViewAdditionalCommandContext;
type O = DeviceViewOuterState;

export type DeviceViewCommandContext = CommandModelCommandContext<S, C, O>;
export type DeviceViewCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type DeviceViewDispatch = CommandModelCommandDispatcher<S, C, O>;
export type DeviceViewMergedState = CommandModelMergedState<S, O>;
export type DeviceViewModel = CommandModel<S, C, O>;

export const createInitialDeviceViewState = (): DeviceViewState => ({
  activeDeviceViewEntityId: null,
  deviceViewEntities: [],
  deviceViewOpen: false,
  deviceViewWindows: [],
  settings: {
    edges: {},
    nodes: {},
    nodesLoaded: false,
    saving: false,
  },
  tabData: {},
});

export const DeviceViewContext = createContext<DeviceViewModel>(undefined!);

export const useDeviceViewContextSelectors: ContextSelectorsHook<
  DeviceViewModel
> = (selectors) => useContextSelectors(DeviceViewContext, selectors);
